/**
 * AI Flashcard Quality Improver — enriches cards with definition, example, clearer translation.
 * Uses Gemini. Does NOT overwrite user input unless clearly incorrect.
 */

import { callGemini } from "./gemini";
import { GEMINI_LANG_NAMES } from "@/types/linguadeck";

export interface EnrichmentResult {
  definition?: string | null;
  example?: string | null;
  back?: string | null; // clearer translation, only if improvement justified
}

const SYSTEM_INSTRUCTION = `You are a language learning assistant. You enrich flashcard data with:
1. DEFINITION: short, clear, learner-friendly definition. CEFR A1–B2 style. One sentence. No jargon.
2. EXAMPLE: natural, simple example sentence using the word. Contextual. Not too long. One sentence.
3. TRANSLATION: only suggest a clearer back/translation if the original is clearly wrong, ambiguous, or unnatural. If the original is fine, return it unchanged.

Rules:
- Do NOT overwrite correct user input. Only enhance missing fields or fix clear errors.
- Definition: simple, clear, short. Example: "A common fruit that grows on trees, often red, green, or yellow."
- Example: natural sentence. Example: "She ate a fresh apple for breakfast."
- Output MUST be valid JSON only. No markdown, no code fences, no extra text.`;

function buildEnrichmentPrompt(
  front: string,
  back: string,
  frontLang: string,
  backLang: string
): string {
  const frontLangName = GEMINI_LANG_NAMES[frontLang] ?? frontLang;
  const backLangName = GEMINI_LANG_NAMES[backLang] ?? backLang;

  return `Word (${frontLangName}): ${front}
Translation (${backLangName}): ${back}

Return a JSON object with:
- "definition": short definition in ${backLangName} (or ${frontLangName} if that fits better for learners). Omit if not applicable (e.g. proper nouns).
- "example": one natural example sentence in ${frontLangName} using the word. Omit if not applicable.
- "back": clearer translation only if the current one is wrong or unclear. Otherwise return the same: "${back}"

Output ONLY valid JSON, e.g.: {"definition":"...","example":"...","back":"..."}`;
}

function extractJsonFromResponse(text: string): EnrichmentResult | null {
  const trimmed = text.trim();
  const cleaned = trimmed
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  try {
    const parsed = JSON.parse(cleaned) as Record<string, unknown>;
    const result: EnrichmentResult = {};
    if (typeof parsed.definition === "string" && parsed.definition.trim()) {
      result.definition = parsed.definition.trim();
    }
    if (typeof parsed.example === "string" && parsed.example.trim()) {
      result.example = parsed.example.trim();
    }
    if (typeof parsed.back === "string" && parsed.back.trim()) {
      result.back = parsed.back.trim();
    }
    return result;
  } catch {
    return null;
  }
}

export async function enrichCardWithAI(
  front: string,
  back: string,
  frontLang: string,
  backLang: string,
  geminiKeys: string[],
  errorMessage: string
): Promise<EnrichmentResult> {
  const prompt = buildEnrichmentPrompt(front, back, frontLang, backLang);
  const text = await callGemini(prompt, geminiKeys, errorMessage, {
    systemInstruction: SYSTEM_INSTRUCTION,
    maxOutputTokens: 400,
    temperature: 0.5,
  });
  const parsed = extractJsonFromResponse(text ?? "");
  return parsed ?? {};
}
